import React, { useCallback, useEffect, useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { FileFieldType } from '@/type';
import useDialogAction from '@components/common/hooks/dialogAction.hooks';

interface FileFieldProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  fileField: FileFieldType;
}

const FileFieldController = <TFieldValues extends FieldValues = FieldValues>({
  control,
  fileField,
}: FileFieldProps<TFieldValues>) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const { openDialog } = useDialogAction();

  const confirmResolutionMismatch = useCallback(
    (
      actualWidth: number,
      actualHeight: number,
      expectedWidth: number,
      expectedHeight: number,
    ) => {
      return new Promise<boolean>((resolve) => {
        openDialog(
          {
            dialogTitle: t('general.bad-resolution'),
            dialogContentText: t(`announcement.new-image-bad-resolution`, {
              width1: actualWidth,
              height1: actualHeight,
              width2: expectedWidth,
              height2: expectedHeight,
            }),
            agreeButtonText: t('general.yes'),
            disagreeButtonText: t('general.no'),
          },
          async (result: boolean) => {
            resolve(result);
          },
        );
      });
    },
    [openDialog, t],
  );

  const handleFileChange = useCallback(
    async (file: File): Promise<string | null> => {
      setFileName(file.name);
      setFileType(file.type);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();

        return new Promise((resolve, reject) => {
          reader.onload = () => {
            const base64 = reader.result as string;
            const base64Data = base64.split(',')[1];

            const img = new Image();
            img.onload = async () => {
              const width = img.width;
              const height = img.height;

              const expectedWidth = fileField?.validateResolution?.expectedWidth;
              const expectedHeight = fileField?.validateResolution?.expectedHeight;

              if (
                expectedWidth &&
                expectedHeight &&
                (width > expectedWidth || height > expectedHeight)
              ) {
                const proceed = await confirmResolutionMismatch(
                  width,
                  height,
                  expectedWidth,
                  expectedHeight,
                );
                if (!proceed) {
                  setInputKey((prev) => prev + 1);
                  resolve(null);
                  return;
                }
              }

              setPreview(base64);
              resolve(base64Data);
            };
            img.onerror = reject;
            img.src = base64;
          };

          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } else {
        setPreview(null);
        const reader = new FileReader();

        return new Promise((resolve, reject) => {
          reader.onload = () => {
            const base64 = reader.result as string;
            const base64Data = base64.split(',')[1];
            resolve(base64Data);
          };

          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
    },
    [fileField],
  );

  const handleDelete = useCallback(() => {
    setPreview(null);
    setFileName(null);
    setInputKey((prevKey) => prevKey + 1);
  }, []);

  const acceptAttribute = fileField.allowedMimeTypes
    ? fileField.allowedMimeTypes.map((mimeType) => `.${mimeType.split('/')[1]}`).join(',')
    : undefined;

  return (
    <Controller
      name={fileField.name as Path<TFieldValues>}
      control={control}
      render={({ field, fieldState }) => {
        const { error } = fieldState;

        useEffect(() => {
          if (field.value && fileType?.startsWith('image/')) {
            setPreview(`data:image/*;base64,${field.value}`);
            setFileName('Uploaded Image');
          }
          if (!field.value) {
            setPreview(null);
            setFileName(null);
          }
        }, [field.value]);

        return (
          <>
            <Button
              variant='contained'
              component='label'
              disabled={fileField.disabled || !!preview}
            >
              {t(fileField.label)}
              <input
                key={inputKey}
                type='file'
                hidden
                accept={acceptAttribute}
                onChange={async (e) => {
                  if (e.target.files && e.target.files[0]) {
                    const base64 = await handleFileChange(e.target.files[0]);
                    if (base64) {
                      field.onChange(base64);
                      fileField?.onChange(base64);
                    }
                  } else {
                    field.onChange('');
                    fileField?.onChange('');
                  }
                }}
              />
            </Button>

            {(preview || fileName) && (
              <div style={{ marginTop: '16px', position: 'relative' }}>
                {preview ? (
                  <img
                    src={preview}
                    alt='Preview'
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                    }}
                  />
                ) : (
                  !fileField.validateResolution && <p>{fileName}</p>
                )}
                <IconButton
                  onClick={() => {
                    handleDelete();
                    field.onChange('');
                    fileField?.onChange('');
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    background: 'white',
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </div>
            )}

            {error && (
              <p style={{ color: 'red', marginTop: '8px' }}>{t(error.message || '')}</p>
            )}
          </>
        );
      }}
    />
  );
};

export default FileFieldController;
